import { format, parseISO, isValid } from 'date-fns';

export const formatDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return 'N/A';
  try {
    const parsed = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(parsed)) return 'Invalid Date';
    return format(parsed, formatStr);
  } catch {
    return 'Invalid Date';
  }
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
};

export const getGradeColor = (grade) => {
  const gradeMap = {
    'A+': 'text-green-700 bg-green-100',
    'A': 'text-green-600 bg-green-50',
    'B+': 'text-blue-700 bg-blue-100',
    'B': 'text-blue-600 bg-blue-50',
    'C+': 'text-yellow-700 bg-yellow-100',
    'C': 'text-yellow-600 bg-yellow-50',
    'D': 'text-orange-600 bg-orange-50',
    'F': 'text-red-700 bg-red-100',
    'E': 'text-red-600 bg-red-50'
  };
  return gradeMap[grade] || 'text-gray-600 bg-gray-100';
};

export const getStatusColor = (status) => {
  const statusMap = {
    pending: 'text-yellow-700 bg-yellow-100 border-yellow-200',
    approved: 'text-green-700 bg-green-100 border-green-200',
    rejected: 'text-red-700 bg-red-100 border-red-200',
    published: 'text-green-700 bg-green-100 border-green-200',
    draft: 'text-gray-700 bg-gray-100 border-gray-200',
    active: 'text-blue-700 bg-blue-100 border-blue-200',
    inactive: 'text-gray-700 bg-gray-100 border-gray-200',
    upcoming: 'text-purple-700 bg-purple-100 border-purple-200',
    completed: 'text-teal-700 bg-teal-100 border-teal-200',
    cancelled: 'text-red-700 bg-red-100 border-red-200'
  };
  return statusMap[status?.toLowerCase()] || 'text-gray-600 bg-gray-100 border-gray-200';
};

export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const calculateGrade = (percentage) => {
  if (percentage >= 90) return 'A+';
  if (percentage >= 80) return 'A';
  if (percentage >= 70) return 'B+';
  if (percentage >= 60) return 'B';
  if (percentage >= 50) return 'C+';
  if (percentage >= 40) return 'C';
  if (percentage >= 33) return 'D';
  return 'F';
};

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Resolve image URLs — Cloudinary URLs are absolute (https://...), local paths need backend prefix.
// blob:/data: URLs (local previews before upload) are used as-is.
const BACKEND_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');
export const resolveImageUrl = (url) => {
  if (!url) return '';
  if (/^(https?|blob|data):/.test(url)) return url;
  return `${BACKEND_URL}${url}`;
};

// Classify a file ({ name, url, mimeType }) so viewers know how to render it
const FILE_KIND_EXTENSIONS = {
  image: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'avif'],
  pdf: ['pdf'],
  office: ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp', 'rtf'],
  video: ['mp4', 'webm', 'mov', 'ogg'],
  audio: ['mp3', 'wav', 'm4a'],
  text: ['txt'],
};

export const getFileExtension = (file) => {
  for (const source of [file?.name, file?.url]) {
    const match = (source || '').split('?')[0].match(/\.([a-z0-9]+)$/i);
    if (match) return match[1].toLowerCase();
  }
  return '';
};

export const getFileKind = (file) => {
  if (file?.kind) return file.kind;
  const mime = file?.mimeType || '';
  if (mime.startsWith('image/') && mime !== 'image/svg+xml') return 'image';
  if (mime === 'application/pdf') return 'pdf';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  const ext = getFileExtension(file);
  return Object.keys(FILE_KIND_EXTENSIONS).find((kind) => FILE_KIND_EXTENSIONS[kind].includes(ext)) || 'other';
};
